import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

const CustomerProfile = ({ onUpdate, onNavigateToDashboard }) => {
  const { user } = useAuth()
  const { t } = useTranslation(['dashboard', 'common'])
  const [profile, setProfile] = useState({
    business_name: '',
    owner_email: '',
    contact_phone: '',
    website: '',
    industry: '',
    description: ''
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')
  const [activeTab, setActiveTab] = useState('profile')

  useEffect(() => {
    fetchProfile()
  }, [user])

  const fetchProfile = async () => {
    if (!user) return

    try {
      setLoading(true)
      
      const { data: business, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('owner_email', user.email)
        .single()

      if (error) throw error

      setProfile({
        business_name: business.business_name || '',
        owner_email: business.owner_email || '',
        contact_phone: business.contact_phone || '',
        website: business.website || '',
        industry: business.industry || '',
        description: business.description || ''
      })
    } catch (error) {
      console.error('Error fetching profile:', error)
      setErrors({ general: t('profile.loadError') })
    } finally {
      setLoading(false)
    }
  }

  const handleProfileChange = (e) => {
    const { name, value } = e.target
    setProfile(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handlePasswordChange = (e) => {
    const { name, value } = e.target
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateProfile = () => {
    const newErrors = {}
    
    if (!profile.business_name.trim()) {
      newErrors.business_name = 'Business name is required'
    }
    
    if (profile.website && !isValidUrl(profile.website)) {
      newErrors.website = 'Please enter a valid website URL'
    }
    
    if (profile.contact_phone && !isValidPhone(profile.contact_phone)) {
      newErrors.contact_phone = 'Please enter a valid phone number'
    }
    
    return newErrors
  }

  const validatePassword = () => {
    const newErrors = {}
    const strongPasswordPattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{7,}$/
    
    if (!passwordData.newPassword) {
      newErrors.newPassword = t('profile.security.newPasswordRequired')
    } else if (!strongPasswordPattern.test(passwordData.newPassword)) {
      newErrors.newPassword = t('profile.security.passwordRequirements')
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = t('validation.passwordMatch', { ns: 'common' })
    }
    
    return newErrors
  }

  const isValidUrl = (url) => {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`)
      return true
    } catch {
      return false
    }
  }

  const isValidPhone = (phone) => {
    const phoneRegex = /^\+?[\d\s\-\(\)]+$/
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10
  }

  const handleProfileSubmit = async (e) => {
    e.preventDefault()
    
    const validationErrors = validateProfile()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    
    setSaving(true)
    setErrors({})
    
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          business_name: profile.business_name,
          contact_phone: profile.contact_phone || null,
          website: profile.website || null,
          industry: profile.industry || null,
          description: profile.description || null
        })
        .eq('owner_email', user.email)
      
      if (error) throw error
      
      setSuccessMessage('Profile updated successfully!')
      setTimeout(() => setSuccessMessage(''), 3000)
      
      if (onUpdate) onUpdate()
      
    } catch (error) {
      console.error('Error updating profile:', error)
      setErrors({ general: 'Failed to update profile. Please try again.' })
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    
    const validationErrors = validatePassword()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    
    setChangingPassword(true)
    setErrors({})
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      })
      
      if (error) throw error
      
      setSuccessMessage(t('profile.security.passwordChanged'))
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
      setTimeout(() => setSuccessMessage(''), 3000)
      
    } catch (error) {
      console.error('Error changing password:', error)
      setErrors({ password: 'Failed to change password. Please try again.' })
    } finally {
      setChangingPassword(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF1B54]"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex">
          <button
            type="button"
            onClick={onNavigateToDashboard}
            className="inline-flex items-center rounded-md bg-[#FF1B54] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#E1174C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54]/30 focus-visible:ring-offset-2"
          >
            {t('navigation.dashboard', { ns: 'common' })}
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'profile'
                ? 'border-[#FF1B54] text-[#FF1B54]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('profile.title')}
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'security'
                ? 'border-[#FF1B54] text-[#FF1B54]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('profile.tabs.security')}
          </button>
        </nav>
      </div>

      <div className="p-6">
        {errors.general && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
            {errors.general}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {/* Business Profile Tab */}
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('profile.businessInformation')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="business_name" className="block text-sm font-medium text-gray-700">
                    {t('profile.fields.businessName')} *
                  </label>
                  <input
                    type="text"
                    id="business_name"
                    name="business_name"
                    value={profile.business_name}
                    onChange={handleProfileChange}
                    required
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54] ${
                      errors.business_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.business_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.business_name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="owner_email" className="block text-sm font-medium text-gray-700">
                    {t('profile.fields.email')}
                  </label>
                  <input
                    type="email"
                    id="owner_email"
                    name="owner_email"
                    value={profile.owner_email}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('profile.emailCannotChange')}</p>
                </div>

                <div>
                  <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700">
                    {t('profile.fields.phone')}
                  </label>
                  <input
                    type="tel"
                    id="contact_phone"
                    name="contact_phone"
                    value={profile.contact_phone}
                    onChange={handleProfileChange}
                    placeholder="+1 (555) 123-4567"
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54] ${
                      errors.contact_phone ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.contact_phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.contact_phone}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                    {t('profile.fields.website')}
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={profile.website}
                    onChange={handleProfileChange}
                    placeholder="https://www.example.com"
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54] ${
                      errors.website ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-red-600">{errors.website}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
                    {t('profile.fields.industry')}
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    value={profile.industry}
                    onChange={handleProfileChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54]"
                  >
                    <option value="">{t('profile.selectIndustry')}</option>
                    <option value="technology">Technology</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="finance">Finance</option>
                    <option value="retail">Retail</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="education">Education</option>
                    <option value="consulting">Consulting</option>
                    <option value="marketing">Marketing</option>
                    <option value="real-estate">Real Estate</option>
                    <option value="food-beverage">Food & Beverage</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    {t('profile.fields.description')}
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={3}
                    value={profile.description}
                    onChange={handleProfileChange}
                    placeholder={t('profile.descriptionPlaceholder')}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54]"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-6 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? t('dataEntry.saving') : t('profile.saveChanges')}
              </button>
            </div>
          </form>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('profile.security.changePassword')}</h3>
              
              {errors.password && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                  {errors.password}
                </div>
              )}
              
              <div className="space-y-4 max-w-md">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    {t('profile.security.newPassword')} *
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54] ${
                      errors.newPassword ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    {t('profile.security.confirmPassword')} *
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54] ${
                      errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={changingPassword}
                className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-6 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {changingPassword ? t('profile.security.changingPassword') : t('profile.security.changePassword')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default CustomerProfile
